import { LegacyUploadResource } from "./LegacyUploadResource"
import { UploadLayout } from "./UploadLayout"
import { UploadResource } from "./UploadResource"

export default class Upload {
  code: string
  mimeType: string
  layout: UploadLayout | null
  media: {
    thumbnail?: string
    medium?: string
    original: string
    video?: string
    videoSmall?: string
    instagramVideo?: string
  }
  createdAt: number
  public: boolean
  deleted: boolean
  approved: boolean
  processed: boolean
  dimensions: {
    width: number
    height: number
    aspect: number
  }
  viewCount: number | null
  links: {
    trackCopyLink: string
    trackDownload: string
    trackFacebook: string
    trackInstagram: string
    trackTwitter: string
    trackLinkedIn: string
    self?: string
    short?: string
    delete?: string
  }

  constructor() {
    this.dimensions = {
      width: 0,
      height: 0,
      aspect: 0
    }
    this.media = {
      original: ""
    }
    this.links = {
      trackCopyLink: "",
      trackDownload: "",
      trackFacebook: "",
      trackInstagram: "",
      trackTwitter: "",
      trackLinkedIn: "",
    }
  }

  static fromResource(resource: UploadResource): Upload {
    let upload = new Upload()
    upload.code = resource.code

    upload.mimeType = resource.mimeType
    upload.layout = resource.layout
    upload.media = {
      thumbnail: resource.media.thumbnail,
      medium: resource.media.medium,
      original: resource.media.original,
      video: resource.media.video,
      videoSmall: resource.media.videoSmall,
      instagramVideo: resource.media.instagramVideo
    }
    upload.dimensions = {
      width: resource.width,
      height: resource.height,
      aspect: resource.width / resource.height
    }

    upload.createdAt = Date.parse(resource.createdAt)
    upload.deleted = resource.deleted
    upload.approved = resource.approved
    upload.processed = resource.processed

    upload.links = {
      trackCopyLink: resource._links.trackCopyLink.href,
      trackDownload: resource._links.trackDownload.href,
      trackFacebook: resource._links.trackFacebook.href,
      trackInstagram: resource._links.trackInstagram.href,
      trackTwitter: resource._links.trackTwitter.href,
      trackLinkedIn: resource._links.trackLinkedIn.href,
      self: resource._links["web:self"].href,
      short: resource._links["web:short"].href,
      delete: resource._links.delete?.href ?? undefined
    }
    upload.validate()

    return upload
  }

  static fromLegacyResource(resource: LegacyUploadResource): Upload {
    let upload = new Upload()
    upload.code = resource.id
    upload.createdAt = resource.timestamp * 1000
    upload.viewCount = resource.view_count
    upload.public = resource.public
    upload.layout = resource.layout

    upload.media = {
      thumbnail: resource.media.thumbnail,
      medium: resource.media.medium,
      original: resource.media.original,
      video: resource.media.video,
      videoSmall: resource.media.video_small,
      instagramVideo: resource.media.instagram_video
    }
    upload.dimensions = resource.dimensions
    upload.mimeType = resource.mime_type
    upload.links = {
      trackCopyLink: resource.track_urls.copy_link,
      trackDownload: resource.track_urls.download,
      trackFacebook: resource.track_urls.facebook,
      trackInstagram: resource.track_urls.instagram,
      trackTwitter: resource.track_urls.twitter,
      trackLinkedIn: resource.track_urls.linked_in,
      self: resource.page,
      delete: resource._links.delete?.href ?? undefined
    }
    upload.validate()

    return upload
  }

  validate() {
    if (this.mimeType.includes("image/")) {
      if (!this.media.original) {
        throw Error("Image uploads should have an image file")
      }
    } else if (this.mimeType.includes("video/")) {
      if (!this.media.video) {
        throw Error("Video uploads should have a video file")
      }
      if (!this.media.videoSmall) {
        throw Error("Video uploads should have a video thumbnail file")
      }
    }
    if (!this.links.self) {
      throw Error("Upload missing link")
    }
  }

  static EXAMPLE_ID_PREFIX = "example_"
  static EXAMPLE_IMAGE_URI = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z/C/HgAGgwJ/lK3Q6wAAAABJRU5ErkJggg=="
  static EXAMPLE_URL = "https://www.simplebooth.com/"

  static example(uniqueId: number | string): Upload {
    let upload = new Upload()
    upload.code = `${Upload.EXAMPLE_ID_PREFIX}${uniqueId}`
    upload.setMedia(Upload.EXAMPLE_IMAGE_URI)
    upload.createdAt = Date.now()
    upload.mimeType = "image/jpeg"
    upload.dimensions = {
      width: 960,
      height: 1280,
      aspect: 0.75
    }

    return upload
  }

  static getExamples(number: number): Upload[] {
    let uploads: Upload[] = []
    for (let i = 0; i < number; i++) {
      uploads.push(Upload.example(i))
    }
    return uploads
  }

  setMedia(original: string, medium?: string, thumbnail?: string) {
    this.media = {
      original: original,
      medium: medium ?? original,
      thumbnail: thumbnail ?? medium ?? original
    }
  }

  isVideo(): boolean {
    return this.mimeType.includes("video/") 
  }

  isGif(): boolean {
    return this.mimeType === "image/gif"
  }

  isStaticImage(): boolean {
    return this.mimeType.includes("image/")  && !this.isGif()
  }

  hasAudio(): boolean {
    return this.layout === UploadLayout.VIDEO
  }
}

export enum MediaQuality {
  LOW = "LOW",
  MEDIUM = "MEDIUM",
  HIGH = "HIGH"
}
export type MediaQualityEnum = keyof typeof MediaQuality;
